import { useEffect, useState } from 'react'

import { Group, Tooltip, Select, Textarea, Radio, Box, Text } from '@mantine/core'
import { DateInput } from '@mantine/dates'

import { useMutation } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { BULK_STAGE_UPDATE } from 'src/graphql/applicants.graphql'
import {
  ApplicantStage,
  DisqualifiedReason,
  HiringStage,
  NotInterestedReason,
} from 'src/graphql/types/applicants'
import IconPlane from 'src/icons/IconPlane'
import { formatSnakeValue } from 'src/lib/formatters'
import { TERMINATED_REASON_OPTIONS } from 'src/lib/terminatedReason.utils'

const BulkStatusDrawer = ({
  applicants,
  refetchQueries = [],
  onClose,
  setLoading,
  setResults,
}) => {
  const [applicantStageDate, setApplicantStageDate] = useState(new Date())
  const [applicantStage, setApplicantStage] = useState(null)
  const [hiringStage, setHiringStage] = useState(null)
  const [reason, setReason] = useState(null)
  const [content, setContent] = useState('')
  const [disqualifiedOrEligible, setDisqualifiedOrEligible] = useState(false)

  // MUTATIONS:
  const [bulkStageUpdate, { loading: bulkStageLoading }] = useMutation(
    BULK_STAGE_UPDATE,
    {
      refetchQueries: refetchQueries,
      onCompleted: (data) => {
        const results = data?.bulkStageUpdate?.applicants || []

        // Propagate results to parent component
        if (setResults) {
          setResults(
            results.length
              ? results.map((result) => ({
                  applicantId: result.applicantId,
                  success: result.success,
                  errorMsg: result.errorMsg,
                }))
              : applicants.map((a) => ({
                  applicantId: a.applicantId,
                  success: true,
                }))
          )
        }

        const successCount = results.filter((r) => r.success).length
        const failureCount = results.filter((r) => !r.success).length
        const totalCount = results.length

        // Edge case: no applicants (shouldn't happen due to UI validation)
        if (totalCount === 0) {
          return
        }

        if (failureCount === 0) {
          // All succeeded
          const message =
            totalCount === 1
              ? 'Applicant transferred successfully'
              : `All ${totalCount} applicants transferred successfully`
          toast(message, 'success')
        } else if (successCount === 0) {
          // All failed
          const message =
            totalCount === 1
              ? 'Failed to transfer applicant'
              : 'Failed to transfer applicants'
          toast(message, 'error')
        } else {
          // Partial success - check if all failures are custom field errors
          const failures = results.filter((r) => !r.success)
          const allAreCustomFieldErrors = failures.every((f) =>
            f.errorMsg?.startsWith('Missing required fields:')
          )

          const failureReason = allAreCustomFieldErrors
            ? 'due to missing required fields'
            : 'Check details for specific errors'

          toast(
            `${successCount} of ${totalCount} applicants transferred successfully. ${failureCount} failed ${failureReason}.`,
            'warning'
          )
        }
      },
      onError: () => {
        if (setResults) {
          setResults(
            applicants.map((a) => ({
              applicantId: a.applicantId,
              success: false,
              errorMsg: 'Failed to update stage',
            }))
          )
        }
        toast('Unable to update applicants, please try again.', 'error')
      },
    }
  )

  useEffect(() => {
    if (setLoading) {
      setLoading(bulkStageLoading)
    }
  }, [bulkStageLoading, setLoading])

  const handleSubmit = () => {
    bulkStageUpdate({
      variables: {
        applicants: applicants.map((applicant) => applicant.applicantId),
        input: {
          applicantStage,
          applicantStageDate,
          ...(applicantStage === ApplicantStage.PROSPECT && { hiringStage }),
          ...(applicantStage === ApplicantStage.DISQUALIFIED && {
            disqualifiedReason: reason,
          }),
          ...(applicantStage === ApplicantStage.NOT_INTERESTED && {
            notInterestedReason: reason,
          }),
          ...(applicantStage === ApplicantStage.TERMINATED && {
            eligibleForRehire: disqualifiedOrEligible,
            terminatedReason: reason,
          }),
          ...(applicantStage === ApplicantStage.DISQUALIFIED && {
            isPermanentlyDisqualified: disqualifiedOrEligible,
            disqualifiedReason: reason,
          }),
          content,
        },
      },
    })
  }

  const configureLabel = () => {
    switch (applicantStage) {
      case ApplicantStage.TERMINATED:
        return 'Termination Reason'
      case ApplicantStage.NOT_INTERESTED:
        return 'Not Interested Reason'
      case ApplicantStage.DISQUALIFIED:
        return 'Disqualification Reason'
      default:
        return 'Select'
    }
  }

  const configureData = () => {
    switch (applicantStage) {
      case ApplicantStage.TERMINATED:
        return TERMINATED_REASON_OPTIONS
      case ApplicantStage.NOT_INTERESTED:
        return Object.keys(NotInterestedReason).map((key) => ({
          value: key,
          label: formatSnakeValue(key),
        }))
      case ApplicantStage.DISQUALIFIED:
        return Object.keys(DisqualifiedReason).map((key) => ({
          value: key,
          label: formatSnakeValue(key),
        }))
      default:
        return []
    }
  }

  const isDisabled = () => {
    if (applicants.length === 0 || !applicantStage || !applicantStageDate) {
      return true
    }

    if (applicantStage === ApplicantStage.PROSPECT && !hiringStage) {
      return true
    }

    if (
      (applicantStage === ApplicantStage.DISQUALIFIED ||
        applicantStage === ApplicantStage.NOT_INTERESTED ||
        applicantStage === ApplicantStage.TERMINATED) &&
      !reason
    ) {
      return true
    }

    if (
      (applicantStage === ApplicantStage.TERMINATED ||
        applicantStage === ApplicantStage.DISQUALIFIED) &&
      disqualifiedOrEligible === null
    ) {
      return true
    }

    return false
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex flex-row items-center justify-between">
          <Text size="xs">Stage</Text>
          <div className="w-[250px]">
            <Select
              value={applicantStage}
              onChange={setApplicantStage}
              data={Object.keys(ApplicantStage).map((key) => ({
                value: key,
                label: formatSnakeValue(key),
              }))}
              required
              placeholder="Select a stage"
              size="xs"
              classNames={{
                input: 'text-xs text-right',
                dropdown: 'border-doubleNickel-gray-200',
                option:
                  'text-xs text-doubleNickel-gray-900 data-[combobox-selected]:bg-transparent data-[combobox-active]:bg-transparent hover:bg-doubleNickel-gray-100',
              }}
            />
          </div>
        </div>

        {applicantStage === ApplicantStage.PROSPECT && (
          <div className="flex flex-row items-center justify-between">
            <Text size="xs">Hiring Stage</Text>
            <div className="w-[250px]">
              <Select
                data={Object.keys(HiringStage).map((key) => ({
                  value: key,
                  label: formatSnakeValue(key),
                }))}
                required
                value={hiringStage}
                onChange={setHiringStage}
                placeholder="Select"
                size="xs"
                classNames={{
                  input: 'text-xs text-right',
                  dropdown: 'border-doubleNickel-gray-200',
                  option:
                    'text-sm text-doubleNickel-gray-900 data-[combobox-selected]:bg-transparent data-[combobox-active]:bg-transparent hover:bg-doubleNickel-gray-100',
                }}
              />
            </div>
          </div>
        )}

        {applicantStage !== ApplicantStage.PROSPECT &&
          applicantStage !== ApplicantStage.HIRED && (
            <div className="flex flex-row items-center justify-between">
              <Text size="xs">{configureLabel()}</Text>
              <div className="w-[250px]">
                <Select
                  key={applicantStage}
                  data={configureData()}
                  value={reason}
                  required
                  onChange={setReason}
                  placeholder="Select"
                  size="xs"
                  classNames={{
                    input: 'text-xs text-right',
                    dropdown: 'border-doubleNickel-gray-200',
                    option:
                      'text-sm text-doubleNickel-gray-900 data-[combobox-selected]:bg-transparent data-[combobox-active]:bg-transparent hover:bg-doubleNickel-gray-100',
                  }}
                />
              </div>
            </div>
          )}

        <div className="flex flex-row items-center justify-between">
          <Text size="xs">Date</Text>
          <div className="w-[250px]">
            <DateInput
              required
              value={applicantStageDate}
              onChange={setApplicantStageDate}
              valueFormat="MM/DD/YYYY"
              placeholder="Select date"
              size="xs"
              classNames={{
                input: 'text-xs text-right',
              }}
            />
          </div>
        </div>

        {(applicantStage === ApplicantStage.TERMINATED ||
          applicantStage === ApplicantStage.DISQUALIFIED) && (
          <div className="flex flex-col gap-2">
            <Text size="xs">
              {applicantStage === ApplicantStage.TERMINATED
                ? 'Is this applicant eligible for rehire?'
                : 'Is this applicant permanently disqualified?'}
            </Text>
            <Radio.Group
              value={disqualifiedOrEligible.toString()}
              className="w-full"
            >
              <div className="grid grid-cols-2 gap-4">
                <Box
                  onClick={() => setDisqualifiedOrEligible(true)}
                  className={`shadow-xs cursor-pointer rounded-lg border px-4 py-3 hover:border-doubleNickel-brand-500 ${
                    disqualifiedOrEligible
                      ? 'border-doubleNickel-brand-500'
                      : 'border-doubleNickel-gray-300'
                  }`}
                >
                  <Radio value="true" label="Yes" className="cursor-pointer" />
                </Box>
                <Box
                  onClick={() => setDisqualifiedOrEligible(false)}
                  className={`shadow-xs cursor-pointer rounded-lg border px-4 py-3 hover:border-doubleNickel-brand-500 ${
                    !disqualifiedOrEligible
                      ? 'border-doubleNickel-brand-500'
                      : 'border-doubleNickel-gray-300'
                  }`}
                >
                  <Radio value="false" label="No" className="cursor-pointer" />
                </Box>
              </div>
            </Radio.Group>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Text size="xs">Notes</Text>
          <Textarea
            value={content}
            placeholder="Additional details or comments (optional)"
            onChange={(event) => setContent(event.currentTarget.value)}
            minRows={3}
            autosize
          />
        </div>
      </div>

      <Group className="mt-auto grid grid-cols-2">
        <Button
          text="Cancel"
          variant="outline"
          onClick={onClose}
          disabled={bulkStageLoading}
        />
        <Tooltip
          disabled={!isDisabled()}
          label="Please select at least one applicant and complete all required fields"
        >
          <Button
            text={'Update'}
            variant="filled"
            lefticon={<IconPlane />}
            disabled={isDisabled()}
            loading={bulkStageLoading}
            onClick={() => {
              handleSubmit()
            }}
          />
        </Tooltip>
      </Group>
    </>
  )
}

export default BulkStatusDrawer
